/* Native GET sorting and links remain usable without section enhancement. */
if (!customElements.get('midi-collection')) {
  customElements.define('midi-collection', class extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.setAttribute('data-enhanced', '');
      this.addEventListener('submit', event => {
        if (!event.target.matches('[data-facet-form]')) return;
        event.preventDefault();
        const url = this.formURL(event.target);
        if (url) this.navigate(url);
      }, options);
      this.addEventListener('change', event => {
        const form = event.target.closest('[data-facet-form]');
        if (form) this.navigate(this.formURL(form));
      }, options);
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-facet-link], .pagination a');
        if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const url = new URL(link.href);
        if (url.origin !== location.origin) return;
        event.preventDefault();
        this.navigate(url, { pagination: !!link.closest('.pagination') });
      }, options);
      window.addEventListener('popstate', () => {
        this.navigate(new URL(location.href), { history: false });
      }, options);
    }

    disconnectedCallback() {
      this.request?.abort();
      this.listeners?.abort();
    }

    formURL(form) {
      const url = new URL(location.href), fields = new FormData(form);
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.') || ['sort_by', 'page', 'section_id', 'q'].includes(key)) url.searchParams.delete(key);
      }
      for (const [key, value] of fields) if (String(value).trim() !== '') url.searchParams.append(key, value);
      url.hash = '';
      return url;
    }

    focusResult() {
      const target = this.querySelector('[data-result-count]') || this.querySelector('.collection-grid');
      if (target) {
        target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
    }

    async navigate(url, options = {}) {
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      // A stalled GET can be retried through the native recovery link; never leave the grid busy indefinitely.
      const timeout = setTimeout(() => controller.abort('timeout'), 15000);
      const active = document.activeElement;
      const activeId = active?.id;
      this.setAttribute('aria-busy', 'true');
      const error = this.querySelector('[data-collection-error]');
      error.hidden = true;
      const fetchURL = new URL(url);
      fetchURL.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(fetchURL, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw Error('Section request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.getElementById(this.id)?.querySelector('[data-collection-content]');
        if (!source) throw Error('Missing collection');
        if (controller.signal.aborted || !this.isConnected) return;
        this.querySelector('[data-collection-content]').replaceWith(source);
        if (options.history !== false) history.pushState({}, '', url);
        const target = options.pagination ? this.querySelector('.collection-grid') :
          this.querySelector('#' + CSS.escape(activeId || 'missing'));
        if (target && target.getClientRects().length) {
          if (!target.matches('a,button,input,select,summary')) target.tabIndex = -1;
          target.focus({ preventScroll: !options.pagination });
        } else this.focusResult();
        this.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true }));
      } catch (e) {
        if ((controller.signal.aborted && controller.signal.reason !== 'timeout') || !this.isConnected) return;
        error.replaceChildren(document.createTextNode(this.dataset.errorMessage + ' '));
        const link = document.createElement('a');
        link.href = url;
        link.textContent = this.dataset.reloadLabel;
        error.append(link);
        error.hidden = false;
        error.tabIndex = -1;
        error.focus();
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) this.removeAttribute('aria-busy');
      }
    }
  });
}
